import { AfterViewInit, Component, DestroyRef, ElementRef, NgZone, OnInit, Renderer2, ViewChild } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { combineLatest, debounceTime, pairwise, startWith } from 'rxjs';
import { filter, first, switchMap } from 'rxjs/operators';
import { OrganizationProfile } from 'app/organization/model/organization-profile';
import { IAppConfig } from 'app/app-config';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { AppViewportScroller } from '@customer-apps/app-state/viewport-scroller';
import { AsyncPipe, ViewportScroller } from '@angular/common';
import { HeaderComponent, HeaderComponent as HeaderComponent_1, headerComponentState } from './core/components/header/header.component';
import { User } from 'app/account/model/user';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as Sentry from '@sentry/angular';
import { UserService } from '@customer-apps/user/user.service';
import { OrganizationService } from '@customer-apps/organization/organization.service';
import { ThemeService } from '@customer-apps/theme/theme.service';
import { PushService } from '@customer-apps/push/push.service';
import { PageTitleService } from '@customer-apps/page-title/page-title.service';
import { ConfigService } from '@customer-apps/config/config.service';
import { AppStateService } from '@customer-apps/app-state/app-state.service';
import { MainMenuComponent } from './core/components/main-menu/main-menu.component';
import { UserMenuComponent } from './core/components/user-menu/user-menu.component';
import { LoadingDirective } from '@styleguide/loading/loading.directive';
import { OfflineMessageComponent } from '@customer-apps/app-state/offline-message/offline-message.component';
import { ActiveWaitListEntriesComponent } from '@customer-apps/offering/components/active-wait-list-entries/active-wait-list-entries.component';
import { TabbarComponent } from './core/components/tabbar/tabbar.component';
import { LayerLoginComponent } from '@customer-apps/user/components/layer-login/layer-login.component';
import { ForegroundPushMessageComponent } from '@customer-apps/push/foreground-push-message/foreground-push-message.component';
import { AksForPushNotificationPermissionsComponent } from '@customer-apps/notification-preferences/aks-for-push-notification-permissions/aks-for-push-notification-permissions.component';

@Component({
  selector: 'kgr-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    MainMenuComponent,
    UserMenuComponent,
    HeaderComponent_1,
    LoadingDirective,
    OfflineMessageComponent,
    ActiveWaitListEntriesComponent,
    RouterOutlet,
    TabbarComponent,
    LayerLoginComponent,
    AsyncPipe,
    ForegroundPushMessageComponent,
    AksForPushNotificationPermissionsComponent,
    UserMenuComponent,
  ],
})
export class AppComponent implements OnInit, AfterViewInit {
  @ViewChild(HeaderComponent) headerComponent!: HeaderComponent;
  @ViewChild('mainLayout', { static: true }) mainLayout!: ElementRef<HTMLDivElement>;

  loading = false;
  loggedIn$ = this.userService.loggedIn$;
  organization$ = this.organizationService.organization$;
  user$ = this.userService.mainUser$;
  isScreenSmallAndDown$ = this.appStateService.isScreenSmallAndDown$;

  headerComponentState: headerComponentState;

  constructor(
    private router: Router,
    private ngZone: NgZone,
    private userService: UserService<User>,
    private organizationService: OrganizationService<OrganizationProfile>,
    private themeService: ThemeService,
    private pushService: PushService,
    private pageTitleService: PageTitleService,
    private configService: ConfigService<IAppConfig>,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private viewportScroller: ViewportScroller,
    private appStateService: AppStateService,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.pushService
      .init(this.configService.config.firebase)
      .pipe(
        switchMap(() => this.userService.mainUserId$.pipe(startWith(null))),
        pairwise(),
      )
      .subscribe(([oldUserId, newUserId]) => {
        if (newUserId && !oldUserId) {
          // "real" login (not just a multiuser switch)
          this.pushService.register();
          this.userService.addLogoutCallback(() => this.pushService.unregister());
        }
      });

    if (this.appStateService.isNative) {
      this.ngZone.runOutsideAngular(() => {
        App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
          if (event.url) {
            this.ngZone.run(() => {
              let parsedUrl = event.url.replace(/.*\/app\//, '/');
              this.router.navigateByUrl(parsedUrl);
            });
          }
        });
      });
    }

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.closeMenus();
    });

    this.pageTitleService.setTitleSuffix(this.configService.config.titleSuffix);
    this.organization$.pipe(debounceTime(50)).subscribe((organization) => {
      if (organization && organization.id && organization.name) {
        this.pageTitleService.setOrganizationTitle(organization.name);
      } else {
        this.pageTitleService.setOrganizationTitle(null);
      }

      Sentry.setTag('organizationId', organization?.id);
      Sentry.setTag('organizationName', organization?.name);

      if (organization && organization.theme) {
        this.themeService.applyTheme(organization.theme);
      } else {
        this.themeService.applyTheme({});
      }

      this.renderer.setAttribute(this.elementRef.nativeElement, 'data-organization-id', '' + organization?.id);
    });

    this.userService.mainUserId$.subscribe((mainUserId) => {
      if (mainUserId) {
        Sentry.setUser({ id: mainUserId });
        this.showLoadingUntilUserAndOrganizationDataHaveBeenLoaded();
      } else {
        Sentry.setUser(null);
      }
    });
  }

  ngAfterViewInit() {
    (this.viewportScroller as AppViewportScroller).setScrollableElement(this.mainLayout.nativeElement);
  }

  private showLoadingUntilUserAndOrganizationDataHaveBeenLoaded() {
    this.loading = true;
    combineLatest([this.userService.mainUser$, this.organizationService.organization$])
      .pipe(
        filter(([mainUser, organization]) => !!mainUser && !!organization),
        first(),
      )
      .subscribe(
        () => (this.loading = false),
        (error) => (this.loading = false),
      );
  }

  // private disableBodyScrolling() {
  //   document.querySelector('html')!.classList.add('is-kgr-modal-open');
  // }
  //
  // private enableBodyScrolling() {
  //   document.querySelector('html')!.classList.remove('is-kgr-modal-open');
  // }

  onHeaderStateChange(headerComponentState: headerComponentState) {
    this.headerComponentState = headerComponentState;
  }

  closeMenus() {
    this.headerComponent.closeMenus();
  }
}
